/**
 * Click-to-play facade for the Cornerstone video section.
 *
 * The section renders only a poster image and a play button, so neither the
 * YouTube/Vimeo player nor an uploaded video file is fetched until someone
 * actually asks for it — which keeps the page fast and avoids setting
 * third-party cookies on people who never press play.
 *
 * Two sources are supported. An uploaded video arrives as markup inside a
 * <template>, which the browser parses but doesn't load, so swapping it in on
 * click is all it takes. A YouTube or Vimeo link arrives as an embed URL that
 * already carries autoplay, so building the iframe starts it straight away.
 * Both autoplay off the back of a real click, so browsers allow sound.
 */
class CsVideo extends HTMLElement {
  connectedCallback() {
    const trigger = this.querySelector('[data-cs-video-play]');
    if (!trigger) return;

    trigger.addEventListener('click', () => this.play(), { once: true });
  }

  play() {
    const hosted = this.querySelector('template[data-cs-video-hosted]');
    if (hosted) {
      this.playHosted(hosted);
      return;
    }

    this.playEmbed();
  }

  playHosted(template) {
    const fragment = template.content.cloneNode(true);
    const video = fragment.querySelector('video');

    this.replaceChildren(fragment);
    if (!video) return;

    // video_tag renders preload="none", which stops some browsers from
    // honouring autoplay — nudge it along, and fall back to the controls if the
    // browser refuses (an autoplay rejection is not an error worth surfacing).
    video.preload = 'auto';
    video.focus();
    const started = video.play();
    if (started) started.catch(() => {});
  }

  playEmbed() {
    const src = this.dataset.embed;
    if (!src) return;

    const iframe = document.createElement('iframe');
    iframe.src = src;
    iframe.title = this.dataset.title || 'Video';
    iframe.loading = 'lazy';
    iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
    iframe.allowFullscreen = true;
    iframe.referrerPolicy = 'strict-origin-when-cross-origin';
    iframe.className = 'cs-video__embed';

    // An iframe with no dimensions is 300x150 intrinsically, which is enough to
    // drag the whole frame down with it if the stylesheet ever fails to load.
    iframe.width = 1600;
    iframe.height = 900;

    this.replaceChildren(iframe);
    iframe.focus();
  }
}

if (!customElements.get('cs-video')) {
  customElements.define('cs-video', CsVideo);
}
